import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    FaBook, FaSearch, FaFolder, FaArrowLeft,
    FaThumbsUp, FaEye, FaChevronRight, FaTags
} from 'react-icons/fa';
import './KnowledgeBasePage.scss';
import * as Sentry from '@sentry/react';

/**
 * KnowledgeBasePage
 *
 * Public-facing knowledge base for all users. Shows published articles
 * with search, category filtering, and helpfulness rating.
 */
const KnowledgeBasePage = () => {
    const { apiFetch } = useAuth();

    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [viewArticle, setViewArticle] = useState(null);
    const [ratingSubmitted, setRatingSubmitted] = useState(new Set());

    const loadArticles = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ status: 'Published', visibility: 'Public' });
            if (search) params.append('search', search);
            if (categoryFilter) params.append('categoryId', categoryFilter);
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base?${params}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) setArticles(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load articles');
        }
        finally { setLoading(false); }
    }, [apiFetch, search, categoryFilter]);

    const loadCategories = useCallback(async () => {
        try {
            const res = await apiFetch('/api/ITServiceDesk/categories');
            if (res.ok) {
                const json = await res.json();
                if (json.success) setCategories(json.data || []);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            /* silent */
        }
    }, [apiFetch]);

    useEffect(() => { loadArticles(); loadCategories(); }, [loadArticles, loadCategories]);

    const loadArticleDetail = async (id) => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base/${id}`);
            if (res.ok) {
                const json = await res.json();
                if (json.success) setViewArticle(json.data);
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load article');
        }
    };

    const handleRate = async (articleId, helpful) => {
        try {
            const res = await apiFetch(`/api/ITServiceDesk/knowledge-base/${articleId}/rate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ helpful })
            });
            if (res.ok) {
                setRatingSubmitted(prev => new Set(prev).add(articleId));
                toast.success('Thank you for your feedback!');
                if (viewArticle?.articleId === articleId) {
                    loadArticleDetail(articleId);
                }
            }
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Rating failed');
        }
    };

    // Group articles by category
    const groupedArticles = useMemo(() => {
        const groups = {};
        articles.forEach(a => {
            const cat = a.categoryName || 'General';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(a);
        });
        return groups;
    }, [articles]);

    if (loading) return <HexagonLoader />;

    // ── Article detail view ──
    if (viewArticle) {
        return (
            <div className="kb-page">
                <button className="kb-back-btn" onClick={() => setViewArticle(null)}>
                    <FaArrowLeft /> Back to Knowledge Base
                </button>
                <article className="kb-article">
                    <h1>{viewArticle.title}</h1>
                    <div className="kb-article-meta">
                        {viewArticle.categoryName && <span><FaFolder /> {viewArticle.categoryName}</span>}
                        <span><FaEye /> {viewArticle.viewCount} views</span>
                        <span><FaThumbsUp /> {viewArticle.helpfulCount} found helpful</span>
                        <span>v{viewArticle.version}</span>
                        {viewArticle.tags && (
                            <span className="kb-tags">
                                <FaTags /> {viewArticle.tags.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
                                    <span key={i} className="kb-tag">{t}</span>
                                ))}
                            </span>
                        )}
                    </div>
                    <div className="kb-article-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewArticle.content}</ReactMarkdown>
                    </div>
                    {!ratingSubmitted.has(viewArticle.articleId) && (
                        <div className="kb-rating-prompt">
                            <span>Was this article helpful?</span>
                            <button className="kb-rate-btn helpful" onClick={() => handleRate(viewArticle.articleId, true)}>
                                <FaThumbsUp /> Yes
                            </button>
                            <button className="kb-rate-btn not-helpful" onClick={() => handleRate(viewArticle.articleId, false)}>
                                <FaThumbsUp style={{ transform: 'rotate(180deg)' }} /> No
                            </button>
                        </div>
                    )}
                    {ratingSubmitted.has(viewArticle.articleId) && (
                        <p className="kb-rating-thanks">Thank you for your feedback!</p>
                    )}
                </article>
            </div>
        );
    }

    // ── List view ──
    return (
        <div className="kb-page">
            <div className="kb-header">
                <h1><FaBook /> Knowledge Base</h1>
                <p>Find answers, how-to guides, and troubleshooting articles.</p>
            </div>

            <div className="kb-toolbar">
                <div className="kb-search-box">
                    <FaSearch />
                    <input
                        placeholder="Search articles..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <select
                    className="kb-category-filter"
                    value={categoryFilter || ''}
                    onChange={e => setCategoryFilter(e.target.value ? parseInt(e.target.value) : null)}
                >
                    <option value="">All Categories</option>
                    {categories.map(c => (
                        <option key={c.categoryId} value={c.categoryId}>{c.fullPath || c.name}</option>
                    ))}
                </select>
            </div>

            {Object.keys(groupedArticles).length === 0 ? (
                <div className="kb-empty">
                    <FaBook />
                    <p>No articles found. Try adjusting your search or filters.</p>
                </div>
            ) : (
                Object.entries(groupedArticles).map(([catName, arts]) => (
                    <div key={catName} className="kb-category-group">
                        <h2><FaFolder /> {catName}</h2>
                        <div className="kb-article-grid">
                            {arts.map(a => (
                                <div key={a.articleId} className="kb-article-card" onClick={() => loadArticleDetail(a.articleId)}>
                                    <h3>{a.title} <FaChevronRight /></h3>
                                    {a.summary && <p>{a.summary}</p>}
                                    <div className="kb-article-card-footer">
                                        <span><FaThumbsUp /> {a.helpfulCount}</span>
                                        <span><FaEye /> {a.viewCount}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default KnowledgeBasePage;
